import { Component, OnDestroy, OnInit, ViewChild, ViewEncapsulation, AfterViewInit } from '@angular/core';

import { fuseAnimations } from '@fuse/animations';
import { Subject, Observable, BehaviorSubject, merge } from 'rxjs';
import { DataSource } from '@angular/cdk/table';
import { takeUntil, tap } from 'rxjs/operators';
import { MatPaginator, MatSort, MatDialog, MatDialogRef, MatSnackBar } from '@angular/material';
import { ComplaintService } from '@fuse/services/complaint/complaint.service';
import { CompanyService } from '@fuse/services/company.service';
import { TranslateService } from '@ngx-translate/core';
import { FuseTranslationLoaderService } from '@fuse/services/translation-loader.service';

@Component({
    selector: 'complaint-list',
    templateUrl: './complaint-list.component.html',
    styleUrls: ['./complaint-list.component.scss'],
    encapsulation: ViewEncapsulation.None,
    animations: fuseAnimations
})
export class ComplaintListComponent implements OnInit, AfterViewInit, OnDestroy {
    private _unsubscribeAll: Subject<any>;

    dataSource: FilesDataSource | null;
    displayedColumns = ['complaint', 'complaint_category_name', 'created_at'];
    complaints: any;
    totalPages = 2;
    totalElements = 0;
    sortField = 'created_at';
    sordOrder = 'desc';
    pageSize = 10;
    lang = 'sr';
    company = { id: null, name: null, chat_token: null };

    @ViewChild(MatPaginator, { static: true }) paginator: MatPaginator;
    @ViewChild(MatSort, { static: true }) sort: MatSort;

    constructor(
        private _complaintService: ComplaintService,
        public _matDialog: MatDialog,
        private _matSnackBar: MatSnackBar,
        private _translate: TranslateService,
        private _translationLoader: FuseTranslationLoaderService,
        private _companyService: CompanyService
    ) {
        this._unsubscribeAll = new Subject();
    }

    private getTranslations(): void {
        this._translationLoader.getLastLang()
            .then(response => {
                this.lang = response['code'];
                this._translationLoader.getTranslations(this.lang, '23');
            })
            .catch(err => Promise.reject(err))
            .then(() => {
            }, err => {
            });
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    loadComplaintsPage(): void {
        this.dataSource.loadComplaints(this.paginator.pageIndex + 1, this.paginator.pageSize, this.sort.active || 'created_at', this.sort.direction || 'desc', '', this.company.id);
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    ngOnInit(): void {

        let firstRun = true;

        this.dataSource = new FilesDataSource(this._complaintService);

        if (!this._companyService.activeCompany) {
            this._companyService.getActiveCompany()
                .then(response => {
                    this.company = response;
                })
                .catch(err => Promise.reject(err))
                .then(() => {
                    this.loadComplaintsPage();
                }, err => {
                });
        } else {
            this.company = this._companyService.activeCompany;
            this.loadComplaintsPage();
        }

        this._complaintService.onComplaintsChanged
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((response) => {
                this.totalPages = Math.floor(response.total / this.paginator.pageSize) + 1;
                this.totalElements = response.total;
                if (firstRun) {
                    firstRun = false;
                } else {
                    this.dataSource.setData(response.data);
                }

            });

        this._translationLoader.onLangChanged
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(value => {
                this.getTranslations();
            });

        this._companyService.onCompanyChanged
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((response) => {
                console.log(this._companyService.activeCompany);
                if (this.company.id !== this._companyService.activeCompany.id ? this._companyService.activeCompany.id : response.id) {
                    this.company = this._companyService.activeCompany;
                    this.loadComplaintsPage();
                }
            });

        this.getTranslations();

    }

    ngAfterViewInit(): void {
        // reset the paginator after sorting
        this.sort.sortChange.subscribe(() => this.paginator.pageIndex = 0);

        // on sort or paginate events, load a new page
        merge(this.sort.sortChange, this.paginator.page)
            .pipe(
                tap(() => this.loadComplaintsPage())
            )
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe();
    }

    ngOnDestroy(): void {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next();
        this._unsubscribeAll.complete();
    }
}

// Custom Angular CDK Data Source
export class FilesDataSource extends DataSource<any>
{
    private complaintsSubject = new BehaviorSubject<any[]>([]);
    private loadingSubject = new BehaviorSubject<boolean>(false);

    public loading$ = this.loadingSubject.asObservable();

    /**
     * Constructor
     *
     * @param {ComplaintService} _complaintService
     */
    constructor(
        private _complaintService: ComplaintService
    ) {
        super();
    }

    connect(): Observable<any[]> {
        return this.complaintsSubject.asObservable();
    }

    disconnect(): void {
        this.complaintsSubject.complete();
        this.loadingSubject.complete();
    }

    setData(data): void {
        this.complaintsSubject.next(data);
        this.loadingSubject.next(false);
    }

    loadComplaints(page: number = 1, limit: number = 20, orderBy: string = 'created_at', order: string = 'desc', search: string = '', company: number = 1): void {
        this.loadingSubject.next(true);
        this._complaintService.listSubmittedComplaints(company, page, limit, orderBy, order, search).then(response => {
            console.log('loadComplaints.');
            this.complaintsSubject.next(response.data);
            this.loadingSubject.next(false);
        });
    }
}
